import React, { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'

import { type AccessReasonEnum } from '~types'

interface RevealPIIModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: AccessReasonEnum) => void
  loading?: boolean
}

const REVEAL_REASONS = [
  {
    value: 'support',
    label: 'Customer Support',
    description: 'For customer service and support inquiries',
  },
  {
    value: 'legal',
    label: 'Legal or Compliance',
    description: 'For legal or regulatory compliance requirements',
  },
  {
    value: 'audit',
    label: 'Audit Review',
    description: 'For internal audit and review purposes',
  },
  {
    value: 'debug',
    label: 'Engineering Debug',
    description: 'For engineering and technical debugging',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'For other authorized business purposes',
  },
]

const RevealPIIModal: React.FC<RevealPIIModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [selectedReason, setSelectedReason] = useState<AccessReasonEnum | ''>('')

  const handleConfirm = (): void => {
    if (selectedReason) {
      onConfirm(selectedReason as AccessReasonEnum)
      setSelectedReason('')
    }
  }

  const handleClose = (): void => {
    setSelectedReason('')
    onClose()
  }

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 3,
        }}
      >
        <Typography variant='h1' sx={{ mb: 2, fontSize: '1.5rem', fontWeight: 500 }}>
          Reveal Personally Identifiable Information (PII)
        </Typography>

        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Please select the reason for revealing PII in this conversation. This action will be
          logged for audit purposes.
        </Typography>

        <FormControl component='fieldset' sx={{ width: '100%', mb: 3 }}>
          <FormLabel
            component='legend'
            sx={{
              mb: 1.5,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.primary',
              '&.Mui-focused': {
                color: 'text.primary',
              },
            }}
          >
            Reason for Access
          </FormLabel>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value as AccessReasonEnum)}
            sx={{ width: '100%' }}
          >
            {REVEAL_REASONS.map((reason) => (
              <FormControlLabel
                key={reason.value}
                value={reason.value}
                control={
                  <Radio
                    size='small'
                    sx={{
                      color: 'text.secondary',
                      '&.Mui-checked': {
                        color: 'secondary.main',
                      },
                      '&.MuiRadio-root:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 500,
                        mb: 0.25,
                      }}
                    >
                      {reason.label}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.3,
                      }}
                    >
                      {reason.description}
                    </Typography>
                  </Box>
                }
                sx={{
                  mb: 1.5,
                  alignItems: 'flex-start',
                  ml: 0,
                  '& .MuiFormControlLabel-asterisk': {
                    display: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                  },
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant='outlined'
            onClick={handleClose}
            disabled={loading}
            sx={{
              minWidth: 100,
              color: 'text.primary',
              borderColor: 'grey.500',
              '&:hover': {
                borderColor: 'grey.700',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleConfirm}
            disabled={!selectedReason || loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Revealing...' : 'Reveal PII'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default RevealPIIModal
